import type { FunctionComponent } from "react";
import type { IUser } from "../../../types/models/user.models.types";
import { useGetUsersHttp } from "../../../hooks/http/admin/users/get-users.admin.http";
import { handleHttpError } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";
import React from "react";
// import { useNavigate } from "react-router-dom";
import type { IPagination } from "../../../types/models/pagination.models.types";
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import Spinner from "../../../components/spinner.components";
import { DataTableComponents } from "../../../components/tables/table.components";
import useUsersStore from "../../../store/users.store";

type IProps = {
  accountType?: string;
  tableType?: string;
  searchFetching?: boolean;
};

export const UsersTableComponents: FunctionComponent<IProps> = ({
  accountType,
  tableType,
  searchFetching,
}) => {
  const [reset, setReset] = React.useState(true);
  const [sortBy, setSortBy] = React.useState("createdAt");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const { data, setData } = useUsersStore();
  // const navigate = useNavigate();
  const [pageNo, setPageNo] = React.useState(1);
  const [paginationData, setPaginationData] = React.useState<IPagination>();

  const {
    cancel: cancelGetUsersHttp,
    fetching,
    execute: getUsersHttp,
  } = useGetUsersHttp();

  const { toast } = useToast();

  async function getUsers(existingUsers: IUser[]) {
    cancelGetUsersHttp();
    const response = await getUsersHttp({
      body: null,
      queryParams: {
        page: pageNo,
        ...(accountType && { accountType }),
        sortBy: sortBy,
        size: 50,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    const newUsers = response.data.users.filter((user) => {
      return existingUsers.findIndex((i) => i.id === user.id) === -1;
    });

    setData([...existingUsers, ...newUsers]);
    setPaginationData(response.data.pagination);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    if (tableType === "search") {
      return;
    }
    if (reset) {
      setReset(false);
      setData([]);
      getUsers([]);
    } else {
      getUsers(data);
    }
  }, [pageNo, sortBy]);

  const columns = React.useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => {
          const { email } = row.original;
          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              <p>{email ?? "-"}</p>
            </div>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 font-semibold capitalize tracking-wider">
              {row.getValue("name") ?? "-"}
            </div>
          );
        },
      },
      {
        accessorKey: "accountType",
        header: "Account Type",
        cell: ({ row }) => {
          const { accountType } = row.original;

          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              <p>{accountType}</p>
            </div>
          );
        },
      },
      {
        accessorKey: "phoneNumber",
        header: "Phone Number",
        cell: ({ row }) => {
          const { phoneNumber } = row.original;

          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              <p>{phoneNumber ?? "-"}</p>
            </div>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  if ((data.length <= 0 && fetching) || searchFetching) {
    return (
      <div className="flex justify-center items-center pt-20 w-full">
        <Spinner />
      </div>
    );
  }

  return (
    <DataTableComponents
      table={table as ReturnType<typeof useReactTable>}
      columns={columns as ColumnDef<IUser>[]}
      setPageNo={setPageNo}
      paginationData={paginationData}
      loading={fetching || searchFetching || false}
      sortBy={sortBy}
      setSortBy={(value) => {
        setData([]);
        setSortBy(value);
      }}
      tableType="users"
    />
  );
};